import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import type { CardAnswer } from '../../lib/card-answer';
import { BLOCKER_TAG_LABEL, type CardBlocker } from '../../lib/machine-card';
import { orgHref } from '../../lib/org';
import { AnswerBox } from './AnswerBox';

// One open blocker on the card's current task (#6785, prototype blkHTML, `:1439-1452`):
// its tag, id, summary and repo; a Parked one also shows its question. "Open blocker" is the
// Blockers page's own deep link (`?blocker=<id>`, `routes/Blockers.tsx` `focusId`); the
// prototype's `/blockers/<id>` path does not exist.
//
// "Answer here" (#6726) sits before it on a Parked prose question, for an admin only
// (`CardBlocker.answerHere`); picks, forms, members and viewers answer on the Blockers page.
// The open box replaces the button, and Cancel gives focus back to it.

export function BlockerRow(props: {
  readonly blocker: CardBlocker;
  readonly answer: CardAnswer;
}): JSX.Element {
  const b = (): CardBlocker => props.blocker;
  const open = (): boolean => props.answer.openId() === b().id;
  let answerButton: HTMLButtonElement | undefined;
  const cancel = (): void => {
    props.answer.close();
    answerButton?.focus();
  };
  return (
    <div class="mission-blocker" data-tag={b().tag}>
      <p class="mission-blocker__head">
        <span class="mission-card__tag" data-tag={b().tag}>
          {t(BLOCKER_TAG_LABEL[b().tag])}
        </span>
        <span class="mission-blocker__id">{b().id}</span>
        <span>{b().summary}</span>
      </p>
      <Show when={b().repo}>{(repo) => <p class="mission-blocker__repo">{repo()}</p>}</Show>
      <Show when={b().question}>
        {(q) => (
          <p class="mission-blocker__question">
            <span>{t('fleet.machines.card_question')}</span>
            {q()}
          </p>
        )}
      </Show>
      <Show when={b().answerHere && open()}>
        <AnswerBox blockerId={b().id} answer={props.answer} onCancel={cancel} />
      </Show>
      <div class="mission-blocker__actions">
        <Show when={b().answerHere && !open()}>
          <button
            ref={answerButton}
            type="button"
            class="mission-blocker__open"
            onClick={() => props.answer.open(b().id)}
          >
            {/* The prototype's `ask` glyph, inline: `@developerz/ui` has no question icon
                (its set is listed in `packages/ui/src/index.ts`), and the card already keeps
                prototype glyphs beside the design system's (`MenuIcon`, brief item 34). */}
            <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
              <g
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" />
                <path d="M6.6 5.7a1.5 1.5 0 112 1.4c-.4.2-.6.5-.6.9M8 9.3v.01" />
              </g>
            </svg>
            {t('fleet.machines.card_answer_here')}
          </button>
        </Show>
        <A
          class="mission-blocker__open"
          href={orgHref(`/blockers?blocker=${encodeURIComponent(b().id)}`)}
          title={t('fleet.machines.card_open_blocker_title', { id: b().id })}
        >
          {t('fleet.machines.card_open_blocker')}
          <span aria-hidden="true">↗</span>
        </A>
      </div>
    </div>
  );
}
